/**
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║                            CounselCore™                                       ║
 * ║              Multi-Tenant SaaS Counseling Management Platform                ║
 * ║                                                                               ║
 * ║  Copyright © 2024-2025 HH2. All Rights Reserved.                            ║
 * ║  PROPRIETARY AND CONFIDENTIAL - HIPAA-Compliant Healthcare Software         ║
 * ║                                                                               ║
 * ║  Knowledge Base - Self-Service Support & Documentation                       ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { 
  Book, Search, ThumbsUp, ThumbsDown, Eye, Plus, Edit2, Trash2, 
  Save, X, Filter, CheckCircle, Clock, FileText, Tag, AlertCircle, ArrowLeft
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const KnowledgeBase = ({ token, user }) => {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [viewMode, setViewMode] = useState('browse'); // 'browse' or 'detail'
  
  const [formData, setFormData] = useState({
    title: '',
    category: 'getting-started',
    content: '',
    tags: '',
    published: false
  });

  const categories = [
    { value: 'all', label: 'All Categories', color: 'gray' },
    { value: 'getting-started', label: '🚀 Getting Started', color: 'green' },
    { value: 'authorizations', label: '📋 Authorizations & Services', color: 'blue' },
    { value: 'billing', label: '💰 Billing & Claims', color: 'purple' },
    { value: 'clinical', label: '🏥 Clinical Tools', color: 'teal' },
    { value: 'scheduling', label: '📅 Scheduling & Calendar', color: 'indigo' },
    { value: 'telehealth', label: '📹 Telehealth', color: 'cyan' },
    { value: 'parent-portal', label: '👨‍👩‍👧 Parent Portal', color: 'pink' },
    { value: 'security', label: '🔒 Security & Compliance', color: 'red' },
    { value: 'technical', label: '⚙️ Technical', color: 'orange' },
    { value: 'account', label: '👤 Account', color: 'yellow' }
  ];

  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  useEffect(() => {
    fetchArticles();
  }, [categoryFilter]);

  useEffect(() => {
    filterArticles();
  }, [searchTerm, articles, categoryFilter]);

  const fetchArticles = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (categoryFilter !== 'all') {
        params.append('category', categoryFilter);
      }
      if (!isAdmin) {
        params.append('published_only', 'true');
      } else {
        params.append('published_only', 'false');
      }

      const response = await fetch(
        `${API}/api/knowledge-base/articles?${params.toString()}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      if (response.ok) {
        const data = await response.json();
        setArticles(data.data || []);
      } else {
        throw new Error('Failed to fetch articles');
      }
    } catch (error) {
      console.error('Error fetching articles:', error);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  };

  const filterArticles = () => {
    let filtered = articles;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        article =>
          article.title.toLowerCase().includes(term) ||
          article.content.toLowerCase().includes(term) ||
          article.tags?.some(tag => tag.toLowerCase().includes(term))
      );
    }

    setFilteredArticles(filtered);
  };

  const handleViewArticle = async (articleId) => {
    try {
      const response = await fetch(
        `${API}/api/knowledge-base/articles/${articleId}`,
        {
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        }
      );

      if (response.ok) {
        const data = await response.json();
        setSelectedArticle(data.data);
        setViewMode('detail');
      } else {
        throw new Error('Failed to fetch article');
      }
    } catch (error) {
      console.error('Error fetching article:', error);
      toast.error('Failed to load article');
    }
  };

  const handleFeedback = async (helpful) => {
    if (!selectedArticle) return;

    try {
      const response = await fetch(
        `${API}/api/knowledge-base/articles/${selectedArticle.id}/feedback`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ helpful })
        }
      );

      if (response.ok) {
        toast.success('Thank you for your feedback!');
        // Update local state
        setSelectedArticle(prev => ({
          ...prev,
          helpful_count: helpful ? prev.helpful_count + 1 : prev.helpful_count,
          not_helpful_count: !helpful ? prev.not_helpful_count + 1 : prev.not_helpful_count
        }));
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback');
    }
  };

  const handleCreateArticle = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }

    try {
      const payload = {
        title: formData.title,
        category: formData.category,
        content: formData.content,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        published: formData.published
      };

      const response = await fetch(`${API}/api/knowledge-base/articles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success('Article created successfully');
        setShowCreateModal(false);
        resetForm();
        fetchArticles();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create article');
      }
    } catch (error) {
      console.error('Error creating article:', error);
      toast.error(error.message || 'Failed to create article');
    }
  };

  const handleEditArticle = async () => {
    if (!formData.title || !formData.content) {
      toast.error('Title and content are required');
      return;
    }

    try {
      const payload = {
        title: formData.title,
        category: formData.category,
        content: formData.content,
        tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
        published: formData.published
      };

      const response = await fetch(
        `${API}/api/knowledge-base/articles/${selectedArticle.id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        toast.success('Article updated successfully');
        setShowEditModal(false);
        resetForm();
        fetchArticles();
        setViewMode('browse');
        setSelectedArticle(null);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update article');
      }
    } catch (error) {
      console.error('Error updating article:', error);
      toast.error(error.message || 'Failed to update article');
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article?')) {
      return;
    }

    try {
      const response = await fetch(
        `${API}/api/knowledge-base/articles/${articleId}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        toast.success('Article deleted successfully');
        fetchArticles();
        if (selectedArticle?.id === articleId) {
          setViewMode('browse');
          setSelectedArticle(null);
        }
      } else {
        throw new Error('Failed to delete article');
      }
    } catch (error) {
      console.error('Error deleting article:', error);
      toast.error('Failed to delete article');
    }
  };

  const openEditModal = (article) => {
    setFormData({
      title: article.title,
      category: article.category,
      content: article.content,
      tags: article.tags?.join(', ') || '',
      published: article.published
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: 'getting-started',
      content: '',
      tags: '',
      published: false
    });
  };

  const getCategoryColor = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat?.color || 'gray';
  };

  const getCategoryLabel = (category) => {
    const cat = categories.find(c => c.value === category);
    return cat?.label || category;
  };

  const renderBrowseView = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Button 
              onClick={() => navigate(-1)} 
              variant="outline" 
              size="sm"
              className="mb-2"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <Book className="w-8 h-8 mr-3 text-teal-600" />
              Knowledge Base
            </h1>
          </div>
          <p className="text-gray-600 mt-2">
            Self-service documentation and support articles
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreateModal(true)} className="bg-teal-600 hover:bg-teal-700">
            <Plus className="w-4 h-4 mr-2" />
            New Article
          </Button>
        )}
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <Input
                type="text"
                placeholder="Search articles, tags, or content..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Category Filter */}
            <div className="flex items-center space-x-2 flex-wrap">
              <Filter className="w-4 h-4 text-gray-500" />
              <span className="text-sm text-gray-600 font-medium">Filter:</span>
              {categories.map((category) => (
                <button
                  key={category.value}
                  onClick={() => setCategoryFilter(category.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    categoryFilter === category.value
                      ? `bg-${category.color}-600 text-white`
                      : `bg-${category.color}-100 text-${category.color}-700 hover:bg-${category.color}-200`
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Articles Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      ) : filteredArticles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">No articles found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArticles.map((article) => (
            <Card
              key={article.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => handleViewArticle(article.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between mb-2">
                  <Badge className={`bg-${getCategoryColor(article.category)}-100 text-${getCategoryColor(article.category)}-700`}>
                    {getCategoryLabel(article.category)}
                  </Badge>
                  {!article.published && isAdmin && (
                    <Badge className="bg-yellow-100 text-yellow-700">
                      Draft
                    </Badge>
                  )}
                </div>
                <CardTitle className="text-lg">{article.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 line-clamp-3 mb-4">
                  {article.content.substring(0, 150)}...
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <div className="flex items-center space-x-4">
                    <span className="flex items-center">
                      <Eye className="w-3 h-3 mr-1" />
                      {article.view_count}
                    </span>
                    <span className="flex items-center">
                      <ThumbsUp className="w-3 h-3 mr-1" />
                      {article.helpful_count}
                    </span>
                  </div>
                  <Clock className="w-3 h-3 mr-1" />
                </div>
                {article.tags && article.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {article.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs"
                      >
                        <Tag className="w-3 h-3 inline mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );

  const renderDetailView = () => {
    if (!selectedArticle) return null;

    return (
      <div className="space-y-6">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => {
              setViewMode('browse');
              setSelectedArticle(null);
            }}
          >
            ← Back to Articles
          </Button>
          {isAdmin && (
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => openEditModal(selectedArticle)}
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Edit
              </Button>
              <Button
                variant="outline"
                className="text-red-600 hover:bg-red-50"
                onClick={() => handleDeleteArticle(selectedArticle.id)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          )}
        </div>

        {/* Article Content */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between mb-4">
              <div>
                <Badge className={`bg-${getCategoryColor(selectedArticle.category)}-100 text-${getCategoryColor(selectedArticle.category)}-700 mb-2`}>
                  {getCategoryLabel(selectedArticle.category)}
                </Badge>
                {!selectedArticle.published && isAdmin && (
                  <Badge className="bg-yellow-100 text-yellow-700 ml-2">
                    Draft
                  </Badge>
                )}
              </div>
            </div>
            <CardTitle className="text-2xl mb-2">{selectedArticle.title}</CardTitle>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>By {selectedArticle.author_name}</span>
              <span>•</span>
              <span className="flex items-center">
                <Eye className="w-4 h-4 mr-1" />
                {selectedArticle.view_count} views
              </span>
              <span>•</span>
              <span>
                {new Date(selectedArticle.created_at).toLocaleDateString()}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap text-gray-700">
                {selectedArticle.content}
              </div>
            </div>

            {selectedArticle.tags && selectedArticle.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-6 pt-6 border-t">
                {selectedArticle.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                  >
                    <Tag className="w-3 h-3 inline mr-1" />
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feedback Section */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Was this article helpful?</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                onClick={() => handleFeedback(true)}
                className="flex items-center space-x-2"
              >
                <ThumbsUp className="w-4 h-4" />
                <span>Helpful ({selectedArticle.helpful_count})</span>
              </Button>
              <Button
                variant="outline"
                onClick={() => handleFeedback(false)}
                className="flex items-center space-x-2"
              >
                <ThumbsDown className="w-4 h-4" />
                <span>Not Helpful ({selectedArticle.not_helpful_count})</span>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  const renderArticleForm = (isEdit = false) => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Article' : 'Create New Article'}
          </h2>
          <button
            onClick={() => {
              isEdit ? setShowEditModal(false) : setShowCreateModal(false);
              resetForm();
            }}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter article title"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category *
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            >
              {categories.filter(c => c.value !== 'all').map((cat) => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Content *
            </label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Write your article content here..."
              rows={12}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags (comma-separated)
            </label>
            <Input
              type="text"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="e.g. setup, billing, tutorial"
            />
          </div>

          {/* Published Toggle */}
          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="published"
              checked={formData.published}
              onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
              className="w-4 h-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
            />
            <label htmlFor="published" className="text-sm text-gray-700">
              Publish immediately (uncheck to save as draft)
            </label>
          </div>
        </div>

        <div className="p-6 border-t flex justify-end space-x-3">
          <Button
            variant="outline"
            onClick={() => {
              isEdit ? setShowEditModal(false) : setShowCreateModal(false);
              resetForm();
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={isEdit ? handleEditArticle : handleCreateArticle}
            className="bg-teal-600 hover:bg-teal-700"
          >
            <Save className="w-4 h-4 mr-2" />
            {isEdit ? 'Update Article' : 'Create Article'}
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-6">
      {viewMode === 'browse' ? renderBrowseView() : renderDetailView()}
      {showCreateModal && renderArticleForm(false)}
      {showEditModal && renderArticleForm(true)}
    </div>
  );
};

export default KnowledgeBase;
